import React, { ChangeEvent, useState } from 'react'

export type isValidFn<T> = (value: unknown, data: T) => boolean

export type requireValueFn<T> = (
    value: string | boolean | number | object,
    data: T
) => boolean

// type requirementCondition = (value: string, data: T) => boolean
interface Validation<T> {
    required?: {
        value: boolean | requireValueFn<T>
        message: string
    }
    pattern?: {
        value: string
        message: string
    }
    custom?: {
        isValid: isValidFn<T>
        message: string
    }
}

type Validations<T extends {}> = Partial<Record<keyof T, Validation<T>>>
interface ChangeWatcher<T> {
    setValue: (prevState: T, currentState: T, newState: T) => T
}

type ChangeWatchers<T extends {}> = Partial<Record<keyof T, ChangeWatcher<T>>>

export type ErrorRecord<T> = Partial<Record<keyof T, string>>

type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>

export const useForm = <T extends PartialRecord<keyof T, any> = {}>(options?: {
    validations?: Validations<T>
    initialValues?: Partial<T>
    changeWatchers?: ChangeWatchers<T>
    onFormSubmit?: (isFormValid: boolean) => void
}) => {
    const [data, setData] = useState<T>((options?.initialValues || {}) as T)
    const [errors, setErrors] = useState<ErrorRecord<T>>({})
    const [shouldValidate, setShouldValidate] = useState<boolean>(false)
    
    const handleChangeEvent =
        <S extends unknown>(key: keyof T, sanitizeFn?: (value: string) => S) =>
        (
            e: ChangeEvent<
                HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
            >
        ) => {
            handleChange(key, e.target.value, sanitizeFn)
        }

    const handleChange = <S extends unknown>(
        key: keyof T,
        targetValue: string | boolean | Date | object | File,
        sanitizeFn?: (value: string) => S
    ) => {
        let value: string | boolean | Date | object

        if (typeof targetValue === 'string') {
            value = sanitizeFn
                ? (sanitizeFn(targetValue) as string)
                : targetValue
        } else {
            value = targetValue
        }

        let newData: T = {
            ...data,
            [key]: value,
        }
        setData((prevState) => {
            newData = triggerWatchers(prevState, data, newData)

            return newData
        })

        if (shouldValidate) {
            validateFields(newData)
        }
    }

    const triggerWatchers = (prevState: T, data: T, newData: T): T => {
        const watchers = options?.changeWatchers
        let hijackedData = { ...newData } as T

        if (watchers) {
            for (const key in watchers) {
                const watcher = watchers[key]

                if (watcher?.setValue) {
                    hijackedData = watcher.setValue(prevState, data, newData)
                }
            }
        }

        return hijackedData
    }

    const handleFormSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()

        const valid = validateFields(data)

        if (options?.onFormSubmit) {
            options.onFormSubmit(valid)
        }
    }

    const enableOnChangeValidation = (shouldEnableValidation: boolean) => {
        setShouldValidate(shouldEnableValidation)
    }

    const validateFields = (data: T): boolean => {
        const validations = options?.validations
        let valid = true

        if (validations) {
            const newErrors: ErrorRecord<T> = {}

            for (const key in validations) {
                const value = data[key]
                const validation = validations[key]
                const isValueBoolean = typeof validation?.required?.value === 'boolean'

                if (isValueBoolean) {
                    if (validation?.required?.value && !value) {
                        valid = false
                        newErrors[key] = validation?.required?.message
                    }
                } else {
                    const required = validation?.required
                    if (required?.value) {
                        if (typeof required?.value === 'function') {
                            if (!required.value(value, data)) {
                                valid = false
                                newErrors[key] = required.message
                            }
                        }
                    }
                }

                const pattern = validation?.pattern
                if (pattern?.value && !RegExp(pattern.value).test(value)) {
                    valid = false
                    newErrors[key] = pattern.message
                }

                const custom = validation?.custom
                if (custom?.isValid && !custom.isValid(value, data)) {
                    valid = false
                    newErrors[key] = custom.message
                }
            }

            if (!valid) {
                setErrors(newErrors)
                return valid
            }
        }

        setErrors({})
        return valid
    }

    const setError = (
        key: Extract<keyof T, string>,
        errorMessage: string | undefined
    ) => {
        const newErrors: ErrorRecord<T> = {}
        newErrors[key] = errorMessage
        setErrors(newErrors)
    }

    return {
        data,
        errors,
        handleChange,
        handleChangeEvent,
        handleFormSubmit,
        enableOnChangeValidation,
        validateFields,
        setError,
        setFormData: setData
    }
}
